/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Backdrop,
  Snackbar,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ApiManager from "../../../../Services/ApiManager/ApiManager";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${theme.breakpoints.down('sm')}`]: {
    padding: "4px 2px",
  },
  padding: "8px 16px",
  borderBottom: "1px solid #c6c6c6",
  borderLeft: "none",
  borderRight: "none",
  width: "50%",
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(even)": {
    backgroundColor: "#f3f3f3",
  },
  "&:hover": {
    backgroundColor: "#f5f5f5",
  },
}));

export default function ViewVehicleListDialog({ 
  open, 
  onClose, 
  vehicleListName,
  portfolioId,
  vehicleListId
}) {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // Helper function for header cell styling
  const getHeaderCellStyle = () => ({
    fontWeight: 600,
    fontSize: "14px",
    color: "#333",
    backgroundColor: "#f8f9fa",
  });

  // Helper function for body cell styling
  const getBodyCellStyle = (bold = false) => ({
    fontSize: "14px",
    color: "#333",
    ...(bold && { fontWeight: "bold" }),
  });

  // Helper function to normalize vehicle response data
  const normalizeVehicleResponse = (response) => {
    if (Array.isArray(response?.data)) {
      return response.data;
    } else if (Array.isArray(response)) {
      return response;
    }
    return [];
  };

  // Fetch vehicles when dialog opens and vehicleListId is available
  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        setLoading(true);
        
        const response = await ApiManager.getVehicleListForGroup(portfolioId, vehicleListId);
        
        setVehicles(normalizeVehicleResponse(response));
      } catch {
        setSnackbarMessage("Failed to load vehicles. Please try again.");
        setOpenSnackbar(true);
        setVehicles([]);
      } finally {
        setLoading(false);
      }
    };

    if (open && portfolioId && vehicleListId) {
      fetchVehicles();
    }
  }, [open, portfolioId, vehicleListId]);

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              maxHeight: "80vh",
            },
          },
        }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 24px",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: "18px",
            fontWeight: 600,
            color: "#333",
          }}
        >
          {vehicleListName || "Vehicle List Name"}{vehicles.length > 0 ? ` (${vehicles.length})` : ""}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            onClick={onClose}
            sx={{
              color: "#666",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ padding: "16px 24px 24px 24px" }}>
        {loading && (
          <Backdrop
            sx={{
              color: "#fff",
              zIndex: (theme) => theme.zIndex.drawer + 1,
              position: "absolute",
            }}
            open={loading}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
        
        {!loading && (
          <div style={{ margin: "8px 8px 8px 0" }}>
            <TableContainer 
              component={Paper} 
              sx={{ 
                boxShadow: "none",
                border: "1px solid #c6c6c6",
                borderRadius: "4px",
                maxHeight: "400px",
                overflowY: "auto"
              }}
            >
            <Table sx={{ minWidth: 500 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                  <StyledTableCell sx={getHeaderCellStyle()}>
                    Vehicle ID
                  </StyledTableCell>
                  <StyledTableCell sx={getHeaderCellStyle()}>
                    Friendly Name
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vehicles.length === 0 ? (
                  <StyledTableRow>
                    <StyledTableCell colSpan={2} align="center" sx={{ py: 2 }}>
                      <Typography sx={{ color: "#717273", fontStyle: "italic" }}>
                        No vehicles found in this list
                      </Typography>
                    </StyledTableCell>
                  </StyledTableRow>
                ) : (
                  vehicles.map((vehicle, index) => (
                    <StyledTableRow key={vehicle.id || vehicle.vehicleId || index}>
                      <StyledTableCell sx={getBodyCellStyle(true)}>
                        {vehicle.id || "N/A"}
                      </StyledTableCell>
                      <StyledTableCell sx={getBodyCellStyle()}>
                        {vehicle.description || "N/A"}
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </div>
        )}
      </DialogContent>
    </Dialog>
    
    {/* Toast for error messages */}
    <Snackbar
      open={openSnackbar}
      autoHideDuration={6000}
      onClose={handleCloseSnackbar}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      message={snackbarMessage}
    />
    </>
  );
}

ViewVehicleListDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  vehicleListName: PropTypes.string,
  portfolioId: PropTypes.string,
  vehicleListId: PropTypes.string,
};
